import React, { FC } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { Divider } from "@material-ui/core";
import { ReactComponent as Intakes } from "../../../../assets/svg/calendar-yellow.svg";
import { ReactComponent as NAIcon } from "../../../../assets/svg/n-a.svg";
import { IntakeType } from "../../constants";
import { IconWithText } from "../IconWithText";
import { IIntakesGrouped } from "../../types";

const useStyles = makeStyles(() => ({
  cardsContainer: {
    marginTop: 16,
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    rowGap: "16px",
  },
  card: {
    border: "1px solid #C5C5C5",
    borderRadius: 15,
    width: 340,
    padding: "16px 6px 16px 32px",
  },
  header: {
    padding: "0 24px 9px 0",
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#957F2F",
  },
  divider: {
    backgroundColor: "#C5C5C5",
    marginTop: 4,
    marginBottom: 7,
  },
  subTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: "#231F20",
    "& + &": {
      marginLeft: 115,
    },
  },
  intakeContainer: {
    display: "flex",
    justifyContent: "space-between",
    "& + &": {
      marginTop: 7,
    },
  },
  scrollableContainer: {
    paddingRight: 26,
    height: 100,
    "overflow-y": "overlay",
  },
  date: {
    fontSize: 16,
    color: "#858997",
  },
  noDataContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingRight: 24,
  },
  noData: {
    fontSize: 17,
    fontWeight: 500,
    color: "#858997",
  },
  noDataIconContainer: {
    paddingTop: 7,
    "& > svg": {
      width: 60,
      height: 60,
    },
  },
}));

type Intake = {
  startDate: string;
  deadline: string;
};

interface IntakeCardProps {
  title: "Fall" | "Spring" | "Summer";
  intakes: Intake[];
}

interface IntakesProps {
  intakes: IIntakesGrouped;
}

const IntakeCard: FC<IntakeCardProps> = ({ title, intakes }) => {
  const classes = useStyles();

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>
        <Divider className={classes.divider} />
        <div>
          <span className={classes.subTitle}>Start Date</span>
          <span className={classes.subTitle}>Deadline</span>
        </div>
      </div>
      {intakes.length ? (
        <div
          className={clsx(classes.scrollableContainer, "transparent-scrollbar")}
        >
          <div>
            {intakes.map((intake) => (
              <div key={intake.startDate} className={classes.intakeContainer}>
                <div className={classes.date}>{intake.startDate}</div>
                <div className={classes.date}>{intake.deadline}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={classes.noDataContainer}>
          <span className={classes.noData}>N/A</span>
          <div className={classes.noDataIconContainer}>
            <NAIcon />
          </div>
        </div>
      )}
    </div>
  );
};

export const IntakesWidget: FC<IntakesProps> = ({ intakes }) => {
  const classes = useStyles();

  return (
    <div>
      <IconWithText Icon={Intakes} title="Intakes" />
      <div className={classes.cardsContainer}>
        <IntakeCard title="Fall" intakes={intakes[IntakeType.FALL]} />
        <IntakeCard title="Spring" intakes={intakes[IntakeType.SPRING]} />
        <IntakeCard title="Summer" intakes={intakes[IntakeType.SUMMER]} />
      </div>
    </div>
  );
};
